import { Group, Rect, Line, Arc } from 'react-konva';

import {

  OPENING_COLORS,

  openingHitPaddingPx,

  resolveOpeningOpacity,

} from '../lib/floorPlanOpening2D.js';

import { resolveSelectableColor } from '../lib/floorPlanSelectableVisualState.js';

import {

  buildDoorSwing2DGeometry,

  resolveDoorSwingFields,

} from '../lib/floorPlanDoorSwing2D.js';



/**

 * @param {{

 *   items: Array<{

 *     id: string;

 *     type: 'door' | 'window';

 *     opening?: import('../lib/floorPlanDesignerState.js').DesignerOpening;

 *     mid: [number, number];

 *     p0: [number, number];

 *     p1: [number, number];

 *     angle: number;

 *     widthM: number;

 *     thicknessM: number;

 *   }>;

 *   mpp: number;

 *   displayScale: number;

 *   activeTool: string;

 *   selectedOpeningId: string | null;

 *   hoverOpeningId: string | null;

 *   onSelectOpening: (openingId: string, e: import('konva/lib/Node').KonvaEventObject<MouseEvent>) => void;

 *   onDragStart: (openingId: string, e: import('konva/lib/Node').KonvaEventObject<DragEvent>) => void;

 *   onDragMove: (openingId: string, e: import('konva/lib/Node').KonvaEventObject<DragEvent>) => void;

 *   onDragEnd: (openingId: string, e: import('konva/lib/Node').KonvaEventObject<DragEvent>) => void;

 *   onHoverOpening: (openingId: string | null) => void;

 *   renderMode?: 'visible' | 'hit';

 * }} props

 */

function FloorPlanOpeningLayer({

  items,

  mpp,

  displayScale,

  activeTool,

  selectedOpeningId,

  hoverOpeningId,

  onSelectOpening,

  onDragStart,

  onDragMove,

  onDragEnd,

  onHoverOpening,

  renderMode = 'visible',

}) {

  const isSelectMode = activeTool === 'select';

  const hitPadding = openingHitPaddingPx(displayScale);

  const strokeWidth = Math.max(1.2 / displayScale, 0.75);



  return items.map((item) => {

    const isSelected = selectedOpeningId === item.id;

    const isHovered = hoverOpeningId === item.id;

    const widthPx = Math.hypot(item.p1[0] - item.p0[0], item.p1[1] - item.p0[1]);

    const thicknessPx = item.thicknessM / mpp;

    const normalColor = item.type === 'door' ? OPENING_COLORS.door : OPENING_COLORS.window;

    const color = resolveSelectableColor({ isSelected, isHovered, normalColor });

    const opacity = resolveOpeningOpacity({
      type: item.type,
      isSelected,
      isHovered,
    });

    if (item.type === 'door' && renderMode === 'visible') {
      const swing = resolveDoorSwingFields(item.opening || {});

      const geometry = buildDoorSwing2DGeometry({
        p0: item.p0,
        p1: item.p1,
        angle: item.angle,
        hingeSide: swing.hingeSide,
        swingSide: swing.swingSide,
        doorState: swing.doorState,
      });

      const arcLineWidth = Math.max(1 / displayScale, 0.6);
      const arcInner = geometry.arc ? Math.max(geometry.arc.radius - arcLineWidth, 0) : 0;

      return (
        <Group key={`opening-visible-${item.id}`} listening={false}>
          <Rect
            x={item.mid[0]}
            y={item.mid[1]}
            width={widthPx}
            height={thicknessPx}
            offsetX={widthPx / 2}
            offsetY={thicknessPx / 2}
            rotation={(item.angle * 180) / Math.PI}
            fill={color}
            opacity={opacity}
            stroke={color}
            strokeWidth={strokeWidth}
            strokeScaleEnabled={false}
          />
          {geometry.leafEnd ? (
            <Line
              points={[geometry.hinge[0], geometry.hinge[1], geometry.leafEnd[0], geometry.leafEnd[1]]}
              stroke={color}
              strokeWidth={strokeWidth}
              strokeScaleEnabled={false}
              lineCap="round"
              opacity={opacity}
            />
          ) : null}
          {geometry.arc ? (
            <Arc
              x={geometry.arc.x}
              y={geometry.arc.y}
              innerRadius={arcInner}
              outerRadius={geometry.arc.radius}
              angle={geometry.arc.angleDeg}
              rotation={geometry.arc.rotationDeg}
              stroke={color}
              strokeWidth={arcLineWidth}
              strokeScaleEnabled={false}
              fillEnabled={false}
              opacity={opacity}
            />
          ) : null}
        </Group>
      );

    }



    if (renderMode === 'visible') {

      return (

        <Group key={`opening-visible-${item.id}`}>

          <Rect

            x={item.mid[0]}

            y={item.mid[1]}

            width={widthPx}

            height={thicknessPx}

            offsetX={widthPx / 2}

            offsetY={thicknessPx / 2}

            rotation={(item.angle * 180) / Math.PI}

            fill={color}

            opacity={opacity}

            stroke={color}

            strokeWidth={isSelected ? 2 / displayScale : 0}

            strokeScaleEnabled={false}

            listening={false}

          />

        </Group>

      );

    }



    let hitWidth = widthPx + hitPadding * 2;

    let hitHeight = thicknessPx + hitPadding * 2;

    let hitX = item.mid[0];

    let hitY = item.mid[1];



    if (item.type === 'door') {

      const swing = resolveDoorSwingFields(item.opening || {});

      const geometry = buildDoorSwing2DGeometry({
        p0: item.p0,
        p1: item.p1,
        angle: item.angle,
        hingeSide: swing.hingeSide,
        swingSide: swing.swingSide,
        doorState: swing.doorState,
      });

      hitWidth = geometry.hitBounds.maxX - geometry.hitBounds.minX + hitPadding * 2;

      hitHeight = geometry.hitBounds.maxY - geometry.hitBounds.minY + hitPadding * 2;

      hitX = (geometry.hitBounds.minX + geometry.hitBounds.maxX) / 2;

      hitY = (geometry.hitBounds.minY + geometry.hitBounds.maxY) / 2;

    }



    return (

      <Group key={`opening-hit-${item.id}`}>

        <Rect

          x={hitX}

          y={hitY}

          width={hitWidth}

          height={hitHeight}

          offsetX={hitWidth / 2}

          offsetY={hitHeight / 2}

          rotation={item.type === 'door' ? 0 : (item.angle * 180) / Math.PI}

          fill="rgba(0,0,0,0.001)"

          draggable={isSelectMode}

          listening={isSelectMode}

          onMouseEnter={() => onHoverOpening(item.id)}

          onMouseLeave={() => onHoverOpening(null)}

          onClick={(e) => {

            if (!isSelectMode) return;

            e.cancelBubble = true;

            onSelectOpening(item.id, e);

          }}

          onTap={(e) => {

            if (!isSelectMode) return;

            e.cancelBubble = true;

            onSelectOpening(item.id, e);

          }}

          onMouseDown={(e) => {

            if (!isSelectMode) return;

            e.cancelBubble = true;

          }}

          onDragStart={(e) => {

            if (!isSelectMode) return;

            e.cancelBubble = true;

            onDragStart(item.id, e);

          }}

          onDragMove={(e) => {

            onDragMove(item.id, e);

          }}

          onDragEnd={(e) => {

            onDragEnd(item.id, e);

          }}

        />

      </Group>

    );

  });

}



export default FloorPlanOpeningLayer;


